import React from 'react';
import { Container, Section, Stack } from '@fiverr-private/layout_components';
import { Tag } from '@fiverr-private/data_display';
import { Image } from '@fiverr-private/media';
import { Typography } from '@fiverr-private/typography';
import { departmentLabel } from '../../i18n';
import { Job } from '../../../lib/comeet';
import { JOB_DETAIL_DESCRIPTION_NAME } from '../../../consts/jobs';
import { RichText } from '../RichText';
import { useBI, useImpression, SECTION_NAMES } from '../../bi';
import { Header } from '../Header';
import { heroImage } from './styles.ve.css';

interface JobHeroSectionProps {
    job: Job;
    heroImage: string;
}

export const JobHeroSection = ({ job, heroImage: heroSrc }: JobHeroSectionProps) => {
    const locationLabel = job.location;
    const description = job.details?.find((j) => j.name === JOB_DETAIL_DESCRIPTION_NAME)?.value;
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() =>
        bi.reportSectionImpression({ sectionName: SECTION_NAMES.jobHero })
    );

    return (
        <Section fullWidth backgroundColor="white">
            <Stack ref={impressionRef} direction="column" paddingX={{ default: '5', md: '14' }} gap="0">
                <Header asLink />

                <Stack
                    direction="column"
                    alignItems="center"
                    gap={{ default: '6', md: '16' }}
                    paddingTop={{ default: '8', md: '20' }}
                >
                    <Stack direction="row" gap="4" justifyContent="center">
                        {job.department ? (
                            <Tag variant="filled" size="sm">
                                {departmentLabel(job.department)}
                            </Tag>
                        ) : null}
                        {locationLabel ? (
                            <Tag variant="filled" size="sm">
                                {locationLabel}
                            </Tag>
                        ) : null}
                    </Stack>

                    <Stack direction="column" alignItems="center" gap={{ default: '4', md: '12' }}>
                        <Typography
                            as="h1"
                            color="black"
                            fontWeight="bold"
                            textAlign="center"
                            maxWidth="725px"
                            size={{ default: 'h_xl', md: 'h_3xl' }}
                            letterSpacing="-0.96px"
                            style={{ lineHeight: 1.1 }}
                        >
                            {job.title}
                        </Typography>
                        {description && (
                            <Container
                                maxWidth="795px"
                                fontSize="h_lg"
                                fontWeight="light"
                                style={{
                                    letterSpacing: '-1.68px',
                                    lineHeight: 1.4,
                                }}
                            >
                                <RichText textAlign="center" html={description} />
                            </Container>
                        )}
                    </Stack>
                </Stack>

                <Container className={heroImage} marginTop={{ default: '8', md: '16' }}>
                    <Image
                        src={heroSrc}
                        alt={job.title}
                        objectFit="cover"
                        width="100%"
                        height="100%"
                        display="block"
                        borderRadius="3xl"
                    />
                </Container>
            </Stack>
        </Section>
    );
};
